import { useState } from 'react';
import { StyledIconButton } from './StyledIconButton';
import PrintIcon from '@mui/icons-material/Print';
import { useLocalization } from '@/hooks/useLocalization';

interface PrintButtonProps {
  /** Place the contentRef on the element(s) that you want printed. */
  contentRef: React.RefObject<HTMLDivElement>;
}

/**
 * Used for printing items other than grids.
 */
export const PrintButton = ({ contentRef }: PrintButtonProps) => {
  const { t } = useLocalization();
  const [_, setIsPrinting] = useState(false);

  const handlePrint = () => {
    const node = contentRef.current;
    if (!node) {
      return;
    }
    setIsPrinting(true);
    const style = document.createElement('style');
    style.innerHTML = `
          @media print {
            /* visibility:hidden keeps elements in the layout, so the hidden app
               shell still takes up height and produces blank pages. Remove
               everything that is neither the printable element, one of its
               ancestors, nor one of its descendants from the layout instead. */
            body *:not(.printable):not(:has(.printable)):not(.printable *) {
              display: none !important;
            }
            /* Ancestors keep rendering, but contribute no offset, size or clipping. */
            *:has(.printable) {
              position: static !important;
              transform: none !important;
              overflow: visible !important;
              margin: 0 !important;
              padding: 0 !important;
              border: 0 !important;
              box-shadow: none !important;
              height: auto !important;
              min-height: 0 !important;
              max-height: none !important;
            }
            .printable, .printable * {
              print-color-adjust: exact !important;
              -webkit-print-color-adjust: exact !important;
              color: #000 !important;
            }
            .printable {
              position: static !important;
              width: 100% !important;
            }
            /* Print every accordion expanded, whatever its on-screen state. MUI
               keeps collapsed content mounted but sizes it to 0 and hides it. */
            .printable .MuiCollapse-root {
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
              visibility: visible !important;
            }
            .printable .MuiCollapse-hidden {
              visibility: visible !important;
            }
            .printable .MuiStack-root{
              padding-top:0 !important;
              margin-top: 0 !important;
              display:block !important;
              margin-bottom:40px !important;
            }
            .printable .MuiBreadcrumbs-ol{
              display:none !important;
            }
            /* Asset Allocation Chart - Left pie chart margin for print */
            .printable .asset-allocation-current-chart{
              margin-bottom:260px !important;
            }
          } 
        `;

    const onAfterPrint = () => {
      node.classList.remove('printable');
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
      window.removeEventListener('afterprint', onAfterPrint);
      setIsPrinting(false);
    };

    window.addEventListener('afterprint', onAfterPrint);
    document.head.appendChild(style);
    node.classList.add('printable');
    setTimeout(() => {
      window.print();
    }, 500);
  };

  return (
    <StyledIconButton
      onClick={handlePrint}
      aria-label={t('print', 'Print')}
    >
      <PrintIcon />
    </StyledIconButton>
  );
};
